import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../../utils/tailwindUtils";
import { fieldControlClass } from "./fieldStyles";
import { Input } from "./input";
import { Button } from "./button";

export const FormLabel = ({ children, htmlFor, className, required = false }) => (
  <label
    htmlFor={htmlFor}
    className={cn("block text-sm font-medium text-foreground mb-1.5", className)}
  >
    {children}
    {required && <span className="text-destructive ml-0.5">*</span>}
  </label>
);

export const SelectField = React.forwardRef(({ className, size = "default", children, ...props }, ref) => (
  <select
    ref={ref}
    className={fieldControlClass(cn("appearance-none cursor-pointer", className), { size })}
    {...props}
  >
    {children}
  </select>
));
SelectField.displayName = "SelectField";

export const PasswordField = React.forwardRef(({
  className,
  label,
  id = "password",
  hideLabel = false,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = React.useState(false);

  return (
    <div className="space-y-1.5">
      {label && !hideLabel && <FormLabel htmlFor={id}>{label}</FormLabel>}
      <div className="relative">
        <Input
          ref={ref}
          id={id}
          type={showPassword ? "text" : "password"}
          className={cn("pr-10", className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setShowPassword((prev) => !prev)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-sidebar-primary transition-colors"
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
});
PasswordField.displayName = "PasswordField";

export const FormCheckbox = ({ label, className, labelClassName, ...props }) => (
  <label className={cn("flex items-center gap-2 cursor-pointer select-none", className)}>
    <input
      type="checkbox"
      className="h-4 w-4 rounded border-input text-sidebar-primary accent-sidebar-primary focus:ring-sidebar-primary/30"
      {...props}
    />
    {label && (
      <span className={cn("text-sm text-muted-foreground", labelClassName)}>{label}</span>
    )}
  </label>
);

export const AuthSubmitButton = ({ loading, loadingText, children, className, ...props }) => (
  <Button
    type="submit"
    variant="brand"
    disabled={loading || props.disabled}
    className={cn("w-full h-10 font-semibold", className)}
    {...props}
  >
    {loading ? loadingText || "Please wait..." : children}
  </Button>
);

export const AuthTextLink = ({ to, children, className, ...props }) => (
  <Link
    to={to}
    className={cn("text-sm font-medium text-sidebar-primary hover:text-sidebar-primary/80 transition-colors", className)}
    {...props}
  >
    {children}
  </Link>
);
